/**
 * Observer Scheduled Calls Component
 * - Shows scheduled calls for the observer
 * - Allows rescheduling calls
 * - WhatsApp message button for parent communication
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar, Clock, Phone, Baby, User, RefreshCw, 
  MessageCircle, Send, Play, Loader2, AlertCircle,
  CheckCircle, XCircle, Edit2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ObserverScheduledCalls() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [showReschedule, setShowReschedule] = useState(null);
  const [showWhatsApp, setShowWhatsApp] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: '',
    reason: ''
  });
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [sending, setSending] = useState(false);

  const getToken = () => localStorage.getItem('observer_token');

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      // Get observer's scheduled calls
      const res = await axios.get(`${API_URL}/api/observer/scheduled-calls?token=${token}`);
      setSchedules(res.data.schedules || []);
    } catch (err) {
      console.error('Error loading schedules:', err);
      toast({
        title: "Error",
        description: "Failed to load scheduled calls",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchedules();
  }, []);

  const getStatusBadge = (status, autoTriggered = false) => {
    switch (status) {
      case 'scheduled':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
            <Clock className="w-3 h-3" /> Scheduled
          </span>
        );
      case 'triggered':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
            {autoTriggered ? '⚡ Auto-Triggered' : 'Triggered'}
          </span>
        );
      case 'completed':
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Completed</span>;
      case 'rescheduled':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">Rescheduled</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Cancelled</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">{status}</span>;
    }
  };

  const handleReschedule = async (scheduleId) => {
    if (!rescheduleForm.new_date || !rescheduleForm.new_time) {
      toast({
        title: "Missing Information",
        description: "Please select new date and time",
        variant: "destructive"
      });
      return;
    }

    try {
      setSending(true);
      const token = getToken();
      
      await axios.post(`${API_URL}/api/observer/reschedule-call?token=${token}`, {
        schedule_id: scheduleId,
        new_date: rescheduleForm.new_date,
        new_time: rescheduleForm.new_time,
        reason: rescheduleForm.reason
      });

      toast({
        title: "Success",
        description: "Call rescheduled successfully"
      });

      setShowReschedule(null);
      setRescheduleForm({ new_date: '', new_time: '', reason: '' });
      loadSchedules();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to reschedule",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendWhatsApp = async (schedule) => {
    if (!whatsappMessage.trim()) {
      toast({
        title: "Missing Message",
        description: "Please enter a message to send",
        variant: "destructive"
      });
      return;
    }

    try {
      setSending(true);
      const token = getToken();
      
      // For now, just store the message - AiSensy integration later
      await axios.post(`${API_URL}/api/observer/send-whatsapp?token=${token}`, {
        schedule_id: schedule.id,
        parent_phone: schedule.parent_phone,
        message: whatsappMessage,
        child_name: schedule.child_name
      });

      toast({
        title: "Message Queued",
        description: "WhatsApp message will be sent to parent"
      });

      setShowWhatsApp(null);
      setWhatsappMessage('');
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const triggerCall = async (scheduleId) => {
    try {
      const token = getToken();
      await axios.post(`${API_URL}/api/observer/trigger-call/${scheduleId}?token=${token}`);
      
      toast({
        title: "Call Initiated",
        description: "Your phone will ring shortly"
      });
      
      loadSchedules();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to trigger call",
        variant: "destructive"
      });
    }
  };

  const getQuickMessages = (childName) => [
    `Hi, this is regarding ${childName}'s scheduled call. I need to reschedule to a different time. Please let me know your availability.`,
    `Hi, I tried calling but couldn't connect. Please confirm a good time for ${childName}'s session today.`,
    `Hi, just a reminder that ${childName}'s session is scheduled for today. Looking forward to speaking with you.`
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-sanjaya-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">My Scheduled Calls</h2>
          <p className="text-sm text-slate-500">View and manage your upcoming calls</p>
        </div>
        <Button variant="outline" onClick={loadSchedules} size="sm">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Scheduled Calls List */}
      <Card>
        <CardContent className="p-0">
          {schedules.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No scheduled calls</p>
              <p className="text-sm">Your upcoming calls will appear here</p>
            </div>
          ) : (
            <div className="divide-y">
              {schedules.map((schedule) => (
                <div key={schedule.id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-sanjaya-orange/10 flex items-center justify-center">
                          <Baby className="w-5 h-5 text-sanjaya-orange" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{schedule.child_name}</p>
                          <p className="text-sm text-slate-500">Parent: {schedule.parent_phone}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-600 ml-13">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {schedule.scheduled_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {schedule.scheduled_time_ist || schedule.scheduled_time} IST
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {schedule.duration_minutes} min
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(schedule.status, schedule.auto_triggered)}
                      
                      {schedule.status === 'scheduled' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => triggerCall(schedule.id)}
                            className="bg-green-600 hover:bg-green-700 text-white h-8"
                          >
                            <Play className="w-3 h-3 mr-1" /> Call Now
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowReschedule(schedule.id);
                              setRescheduleForm({
                                new_date: schedule.scheduled_date,
                                new_time: schedule.scheduled_time,
                                reason: ''
                              });
                            }}
                            className="h-8"
                          >
                            <Edit2 className="w-3 h-3 mr-1" /> Reschedule
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowWhatsApp(schedule);
                              setWhatsappMessage('');
                            }}
                            className="h-8 text-green-600 border-green-600 hover:bg-green-50"
                          >
                            <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                          </Button>
                        </div>
                      )}

                      {schedule.status === 'triggered' && (
                        <span className="text-green-600 flex items-center gap-1 text-sm">
                          <Phone className="w-4 h-4 animate-pulse" />
                          In Progress
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Reschedule Form */}
                  {showReschedule === schedule.id && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                      <h4 className="font-medium mb-3">Reschedule Call</h4>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <Label>New Date</Label>
                          <Input
                            type="date"
                            value={rescheduleForm.new_date}
                            onChange={(e) => setRescheduleForm({...rescheduleForm, new_date: e.target.value})}
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div>
                          <Label>New Time (IST)</Label>
                          <Input
                            type="time"
                            value={rescheduleForm.new_time}
                            onChange={(e) => setRescheduleForm({...rescheduleForm, new_time: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <Label>Reason (optional)</Label>
                        <Textarea
                          value={rescheduleForm.reason}
                          onChange={(e) => setRescheduleForm({...rescheduleForm, reason: e.target.value})}
                          placeholder="Why are you rescheduling?"
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleReschedule(schedule.id)}
                          disabled={sending}
                          className="bg-sanjaya-orange hover:bg-sanjaya-orange/90"
                        >
                          {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                          Confirm Reschedule
                        </Button>
                        <Button variant="outline" onClick={() => setShowReschedule(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* WhatsApp Message Form */}
                  {showWhatsApp?.id === schedule.id && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium mb-3 flex items-center gap-2 text-green-800">
                        <MessageCircle className="w-5 h-5" />
                        Send WhatsApp to Parent
                      </h4>
                      
                      {/* Quick Message Templates */}
                      <div className="mb-3">
                        <Label className="text-sm text-slate-600">Quick Messages:</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {getQuickMessages(schedule.child_name).map((msg, idx) => (
                            <Button
                              key={idx}
                              size="sm"
                              variant="outline"
                              onClick={() => setWhatsappMessage(msg)}
                              className="text-xs h-7"
                            >
                              Template {idx + 1}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <Label>Message</Label>
                        <Textarea
                          value={whatsappMessage}
                          onChange={(e) => setWhatsappMessage(e.target.value)}
                          placeholder="Type your message to the parent..."
                          rows={3}
                          className="bg-white"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleSendWhatsApp(schedule)}
                          disabled={sending || !whatsappMessage.trim()}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                          Send WhatsApp
                        </Button>
                        <Button variant="outline" onClick={() => setShowWhatsApp(null)}>
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        * Message will be sent via WhatsApp to {schedule.parent_phone}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
